import { Box, Divider, Typography } from '@mui/material';
import { updateCoUserPermissions } from 'entities/CoUser/api';
import { CoUser, FileAccessType } from 'entities/CoUser/types';
import { getCompanyUsersInfo } from 'entities/User/api';
import { Form, Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Button } from 'shared/ui/Button';
import { Paper } from 'shared/ui/Paper';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { PermissionsFormValues } from './types';

interface PermissionsProps {
	coUser: CoUser | null;
}

export const Permissions: FC<PermissionsProps> = ({ coUser }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const { mutate, isLoading } = useMutation({
		mutationKey: ['patch/co-users/permissions', coUser?._id],
		mutationFn: updateCoUserPermissions,
	});

	const handleSubmit = (values: PermissionsFormValues) => {
		if (!coUser?._id) return;
		mutate(
			{
				id: coUser._id,
				data: {
					...values,
					accessibleUserIds: values.fileAccessType === 'SELECTED' ? values.accessibleUserIds : [],
				},
			},
			{
				onSuccess: async () => {
					await queryClient.refetchQueries({ queryKey: ['co-users'] });
					enqueueSnackbar({ message: t('shared_texts.success_update'), variant: 'success' });
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const response = useQuery({
		queryKey: ['get/users/company-users'],
		queryFn: getCompanyUsersInfo,
	});

	const userOptions: SelectOption[] = useMemo(
		() =>
			response.data?.data
				? response.data?.data
						.filter((u) => u._id !== coUser?.userId?._id)
						.map((u) => ({
							value: u._id,
							label: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
						}))
				: [],
		[response, coUser?.userId?._id],
	);

	return (
		<>
			<Typography variant={'h5'} color={ColorPalette.astronaut} sx={{ margin: '44px 0 38px 0' }}>
				{t('co_users.tab_4.title')}
			</Typography>

			<Formik
				initialValues={{
					fileAccessType: coUser?.fileAccessType ?? 'OWN',
					accessibleUserIds: coUser?.accessibleUserIds ?? [],
				}}
				onSubmit={handleSubmit}
			>
				{({ values, setFieldValue }) => (
					<Form>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
							<Paper sx={{ width: '100%', padding: 4 }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										gap: 4,
									}}
								>
									<Typography
										variant="body1"
										color={ColorPalette.astronaut}
										sx={{ flex: '1 1' }}
									>
										{t('co_users.tab_4.file_access_type_label')}
									</Typography>
									<FormSelect
										name="fileAccessType"
										label=""
										placeholder=""
										options={fileAccessTypeOptions(t)}
										value={values.fileAccessType}
										onSelect={(value) =>
											setFieldValue('fileAccessType', value as FileAccessType)
										}
										sx={{ flex: '1 1', marginBottom: 0 }}
										sxError={{ margin: 0 }}
									/>
								</Box>

								{values.fileAccessType === 'SELECTED' && (
									<>
										<Divider
											color={ColorPalette.periwinkle}
											sx={{ marginY: 2, borderColor: 'transparent' }}
										/>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between',
												gap: 4,
											}}
										>
											<Typography
												variant="body1"
												color={ColorPalette.astronaut}
												sx={{ flex: '1 1' }}
											>
												{t('co_users.tab_4.accessible_users_label')}
											</Typography>
											<FormSelect
												name="accessibleUserIds"
												label=""
												placeholder={
													t('co_users.tab_4.accessible_users_placeholder') as string
												}
												options={userOptions}
												value={values.accessibleUserIds}
												multiple
												onSelect={(value) =>
													setFieldValue('accessibleUserIds', value as string[])
												}
												sx={{ flex: '1 1', marginBottom: 0 }}
												sxError={{ margin: 0 }}
											/>
										</Box>
									</>
								)}
							</Paper>

							<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Button size="small" type="submit" loading={isLoading}>
									{t('co_users.tab_4.save')}
								</Button>
							</Box>
						</Box>
					</Form>
				)}
			</Formik>
		</>
	);
};

const fileAccessTypeOptions = (t: (key: string) => string): SelectOption[] => [
	{ value: 'OWN', label: t('co_users.tab_4.file_access_type_own') },
	{ value: 'ALL', label: t('co_users.tab_4.file_access_type_all') },
	{ value: 'SELECTED', label: t('co_users.tab_4.file_access_type_selected') },
];
